import {
  type CaptureFormat,
  type CaptureTransformations,
  type EventMap,
  Events,
  type ImageCapture,
} from "@/app/api"
import type { AppConfig } from "@/config"
import { LiqaError } from "@/errors"
import { blobToImage, destroyImage, imageToBlob } from "@/libs/image"
import Styles from "./styles.css?inline"
import { ShadowRealm, Vite, attrs2props } from "./utils"

const liqa = Vite.dependencies(() => import("@/root"))
const errors = Vite.dependencies(() => import("@/libs/telemetry/errors"))
const iframeUrl = (
  __DEV__
    ? new URL("/liqa-iframe.html", import.meta.url)
    : new URL("./liqa-iframe.html", import.meta.url)
).href
const iframeOrigin = new URL(iframeUrl).origin

type Widget = {
  destroy: () => void
  reset?: () => void
  upload?: (files: File[]) => void
}

type IframeMessage<T = unknown> = {
  type: string
  detail?: T
}

type SerializedCapture = {
  id: string
  image: ArrayBuffer
  type: string
  metadata: ImageCapture["metadata"]
}

type AnonymizedResult = NonNullable<ImageCapture["anonymized"]> extends () => Promise<infer R>
  ? R
  : never

type SerializedAnonymized = {
  blobs: Array<{
    type: AnonymizedResult["blobs"][number]["type"]
    data?: ArrayBuffer
    mime?: string
  }>
  transformSequence: AnonymizedResult["transformSequence"]
  mesh: AnonymizedResult["mesh"]
  colorCorrectionCoefficients?: AnonymizedResult["colorCorrectionCoefficients"]
}

type PendingAnonymized = {
  resolve: (result: AnonymizedResult) => void
  reject: (error: unknown) => void
}

type CaptureProxy = ImageCapture & {
  toBlob: (format?: CaptureFormat) => Promise<Blob>
}

const isTruthyAttr = (value: unknown) => !!value && String(value) !== "false"

const normalizeIframeStyles = (styles?: string) =>
  styles ? styles.replace(/:host\([^)]*\)/g, ":root").replace(/:host\b/g, ":root") : undefined

const buildIframeStyles = (host: HTMLElement, styles?: string) => {
  const computedStyle = getComputedStyle(host)
  const vars = ["--background-color", "--color", "--color-brand", "--font-family"]
    .map((name) => [name, computedStyle.getPropertyValue(name).trim()])
    .filter(([, value]) => value)

  const varsCss =
    vars.length > 0
      ? `:root{${vars.map(([name, value]) => `${name}: ${value};`).join("")}}`
      : undefined

  const normalized = normalizeIframeStyles(styles)

  return [varsCss, normalized].filter(Boolean).join("\n") || undefined
}

const deserializeAnonymized = (payload: SerializedAnonymized): AnonymizedResult => ({
  blobs: payload.blobs.map((blob) => ({
    type: blob.type,
    data: blob.data ? new Blob([blob.data], { type: blob.mime }) : undefined,
  })),
  transformSequence: payload.transformSequence,
  mesh: payload.mesh,
  colorCorrectionCoefficients: payload.colorCorrectionCoefficients,
})

const deserializeError = (detail: unknown): LiqaError => {
  if (detail instanceof Error) return detail as LiqaError

  if (!detail || typeof detail !== "object")
    return new LiqaError(String(detail ?? "Unknown error"))

  const data = detail as Partial<{
    message: string
    stack: string
    code: number
    metadata: Record<string, unknown>
  }>
  const error = new LiqaError(data.message ?? "Unknown error", {
    code: data.code,
    metadata: data.metadata,
  })

  if (data.stack)
    Object.defineProperty(error, "stack", { value: data.stack, configurable: true })

  return error
}

const isMessage = (data: unknown): data is IframeMessage =>
  !!data && typeof data === "object" && typeof (data as IframeMessage).type === "string"

const createDeferred = () => {
  let resolve: () => void
  const promise = new Promise<void>((resolver) => {
    resolve = resolver
  })
  return { promise, resolve: resolve! }
}

const buildConfig = (attrs: LiqaAttributes & { useIframe?: string | boolean }): AppConfig => {
  const { useIframe: _useIframe, ...rest } = attrs

  return {
    ...rest,
    effects: isTruthyAttr(rest.effects),
    settings: (rest as any).settings ? JSON.parse((rest as any).settings) : undefined,
    messages: rest.messages ? JSON.parse(rest.messages) : undefined,
    sources: rest.sources?.split(",").map((s) => s.trim()) as AppConfig["sources"],
    qualityCheckSources: (rest as Record<string, string | undefined>).qualityCheckSources
      ?.split(",")
      .map((s) => s.trim()) as AppConfig["qualityCheckSources"],
  }
}

const createCaptureProxy = ({
  serialized,
  requestAnonymized,
  transformations = {},
}: {
  serialized: SerializedCapture
  requestAnonymized: (captureId: string) => Promise<AnonymizedResult>
  transformations?: CaptureTransformations
}): CaptureProxy => {
  const baseBlob = new Blob([serialized.image], { type: serialized.type })
  const metadata = serialized.metadata

  const blob = async (format?: CaptureFormat): Promise<Blob> => {
    if (!transformations.horizontalFlip && !format) return baseBlob

    const resolvedFormat =
      format ?? ((baseBlob.type?.replace("image/", "") as CaptureFormat | "") || "jpeg")
    const targetMime = `image/${resolvedFormat}`

    if (!transformations.horizontalFlip && baseBlob.type === targetMime) return baseBlob

    const image = await blobToImage(baseBlob)

    try {
      return await imageToBlob(image, {
        format: resolvedFormat,
        horizontalFlip: transformations.horizontalFlip,
      })
    } finally {
      destroyImage(image)
    }
  }

  const capture: CaptureProxy = {
    get source() {
      return metadata.source
    },
    get metadata() {
      return metadata
    },
    blob,
    transform: (next) =>
      createCaptureProxy({
        serialized,
        requestAnonymized,
        transformations: { ...transformations, ...next },
      }),
    anonymized: () => requestAnonymized(serialized.id),
    toBlob: (format?: CaptureFormat) => {
      console.warn(
        `[LIQA] Deprecation warning: "ImageCapture.toBlob()" has been deprecated. Use "ImageCapture.blob()" instead.`,
      )

      return blob(format)
    },
  }

  return capture
}

/**
 * The Web component name.
 * @category Misc
 */
export const NAME = "hautai-liqa" as const

type Sources =
  | "front_camera"
  | "back_camera"
  | "upload"
  | "companion"
  | "front_camera,back_camera"
  | "front_camera,upload"
  | "front_camera,upload,companion"
  | "front_camera,companion"
  | "front_camera,back_camera,upload"
  | "front_camera,back_camera,companion"
  | "front_camera,back_camera,upload,companion"
  | "back_camera,upload"
  | "back_camera,companion"
  | "back_camera,upload,companion"
  | "upload,companion"

/**
 * LIQA Web component HTML attributes to configure the component look and behavior.
 * @example
 * ```html
 * <hautai-liqa
 *   license="xxx-xxx-xxx"
 *   styles="@import url('/path/to/custom-liqa-styles.css')"
 * >
 * </hautai-liqa>
 * ```
 * @category Component
 */
export type LiqaAttributes = {
  /** The license key obtained from {@link https://haut.ai/#contacts Haut.ai }. */
  license: string
  /**
   * A string containing valid CSS rules. For more information, see [Styles](/customization/ui/styles) page.
   * @example
   * "@import url('/path/to/custom-liqa-styles.css')"
   */
  styles?: string
  /**
   * Render LIQA inside an iframe instead of Shadow DOM.
   * @default false
   */
  "use-iframe"?: boolean | "true" | "false"
  /**
   * A string containing a valid JSON object whose keys are messages keys and values are messages texts. For more information, see [Text Messages](/customization/ui/text-messages) page.
   * @example
   * "{ \"preview\": { \"submit\": \"Confirm\" } }"
   */
  messages?: string
  /**
   * A preset used to customize AI quality criteria and user flow. For more information, see [Presets](customization/user-flow/presets) page.
   * @default "face"
   */
  preset?: "face" | "face-180" | "hair"
  /**
   * A list of sources to capture the image from: different device cameras or file upload. They can be combined using `,` separator.
   * Visit the corresponding [user flow customization](/customization/user-flow/image-sources) section to learn about their logic and limitations.
   *
   * @default "front_camera"
   * @alias `"camera"`
   * Starting from `6.7.0` version `"camera"` acts as an alias for `"front_camera"`.
   *
   * _Note: For_ `face-180` _preset only_ `"front_camera"` _source is available. The rest will be ignored._
   */
  sources?: Sources
  /**
   * Image capture mode for `"camera"` source.
   * `"auto"` means LIQA will automatically take a photo when all AI quality criteria met.
   * `"manual"` means LIQA will allow the user to take a photo on their own via a button.
   *
   * _Note: The option is only applicable to_ `"hair"` _preset. For other presets it is always_ `"auto"`_._
   */
  capture?: "auto" | "manual"
  /**
   * Lighting requirements for the image capture.
   * - `"none"`: no requirements are set and lighting will not be validated. (**WARNING**: the quality of the processing results may be affected)
   * - `"adaptive"`: the requirements will be softened if the lighting conditions are poor.
   * - `"default"`: the requirements will be set to optimal which will provide more details on the photo.
   *
   * _Note: The option is only applicable to_ `"face"` _preset. For other presets it is always_ `"default"`_._
   *
   * @default `"default"`
   */
  "required-lighting"?: "none" | "adaptive" | "default"
  /**
   * Image post-processing settings.
   *
   * Accepts a comma-separated list of flags:
   * - `"original"`: the image from `"capture"` will not be processed.
   * - `"anonymized"`: the image from `"capture"` will be anonymized (only skin mask will be returned).
   *
   * @default `"original"`
   */
  postprocessing?: string
  /**
   * A flag to enable or disable the effects during the image capture process.
   *
   * @default false
   */
  effects?: boolean | "true" | "false"
  /**
   * A flag to enable or disable tutorial option on the source selection screen.
   * Enabled only when sources option contain "front_camera" or "back_camera" and "upload".
   *
   * - `"video"`: a video tutorial will be shown.
   * - `"image"`: an image tutorial will be shown. Can be used as a fallback for reducing the loading time.
   *
   * @default "video"
   */
  tutorial?: string | "image" | "video"
  /**
   * A flag to set the entry point of the LIQA.
   *
   * - `"capture"`: the entry point will be set to the capture screen.
   * - `"tutorial"`: the entry point will be set to the tutorial screen.
   * - `"tutorial-alt"`: the entry point will be set to the source selection screen with the tutorial shown immediately
   *   (with a framed tutorial layout on desktop). Requires `tutorial="video"` or `tutorial="image"` (not `"off"`).
   *
   * @default "capture"
   */
  "entry-point"?: string | "capture" | "tutorial" | "tutorial-alt"
  /**
   * A flag to set the quality check behavior.
   *
   * - `off`: the quality check will be disabled.
   * - `strict`: the quality check will be strict and the user will not be able to continue if the quality criteria are not met.
   * - `normal`: the quality check will be normal and the user will be able to continue even if the quality criteria are not met.
   *
   * @default `"off"`
   */
  "quality-check"?: "off" | "normal" | "strict"
  /**
   * A flag to set the occlusion detection behavior for the post-capture step (available for the `"face"` preset).
   *
   * - `off`: skips occlusion detection completely.
   * - `strict`: runs occlusion detection after capture; occlusion issues block submission.
   * - `normal`: runs occlusion detection after capture; occlusion issues are shown as a warning but do not block submission.
   *
   * Detection signals:
   * - Flags ~10%+ forehead coverage (bangs/hat/brim).
   * - Flags glasses; detection aims for sensitivity and may not be perfect in all cases.
   *
   * @default `"off"`
   */
  "occlusion-detection"?: "off" | "normal" | "strict"
  /**
   * An array specifying which capture sources should enforce quality checks.
   *
   * The quality check behavior (set via the `qualityCheck` property) will only be applied for the sources included in this array.
   * Available values are:
   * - `front_camera`: Enforces quality check on images captured from the front camera.
   * - `back_camera`: Enforces quality check on images captured from the back camera.
   * - `upload`: Enforces quality check on images provided via file upload.
   * - `companion`: Enforces quality check on images captured via a companion source.
   *
   * If not provided, quality checks will be applied for all sources.
   *
   * @default ["front_camera","back_camera","upload","companion"]
   */
  "quality-check-sources"?: Sources
  /**
   * A flag to enable or disable best frame selection during image capture.
   *
   * When enabled, LIQA will track multiple frames during the capture process
   * and automatically select the frame with the highest quality metrics.
   *
   * @default `"true"`
   */
  "best-frame-selection"?: "true" | "false"
}

/**
 * The component event names and corresponding event details.
 * @example
 * ```html
 * <hautai-liqa
 *   license="xxx-xxx-xxx"
 *   onready="console.log('LIQA is ready!')"
 *   onerror="console.warn('An error occurred:', event.detail)"
 * >
 * </hautai-liqa>
 * ```
 * @category Component
 */
export type {
  AnalyticsDetails,
  AnalyticsEventsMap,
  CaptureFormat,
  CaptureMetadata,
  CaptureTransformations,
  ErrorCodes,
  ImageCapture,
  LiqaError,
} from "@/root"
export type { EventMap as LiqaEventMap }

// TODO: document all the black magic going here and particularly why it's implemented as it's implemented

/**
 * LIQA Web component available as `<hautai-liqa></hautai-liqa>` tag.
 * @example
 * ```html
 * <hautai-liqa license="xxx-xxx-xxx"></hautai-liqa>
 * ```
 * @category Component
 */
export class LiqaElement extends HTMLElement {
  readonly #root: ShadowRoot
  #widget: Promise<Widget> | null
  #iframe: HTMLIFrameElement | null
  #iframeConfig: AppConfig | null
  #iframeConfigured: { promise: Promise<void>; resolve: () => void } | null
  #pendingAnonymized: Map<string, PendingAnonymized>
  #anonymizedRequestId: number

  get #isE2e() {
    return typeof __LIQA_E2E__ !== "undefined" && __LIQA_E2E__
  }

  /** @hidden */
  constructor() {
    super()

    this.#root = this.attachShadow({ mode: this.#isE2e ? "open" : "closed" })
    this.#widget = null
    this.#iframe = null
    this.#iframeConfig = null
    this.#iframeConfigured = null
    this.#pendingAnonymized = new Map()
    this.#anonymizedRequestId = 0

    // new CSSStyleSheet() API is not available on Safari < 16.4
    const style = document.createElement("style")
    style.innerHTML = Styles
    this.#root.appendChild(style)
  }

  #postMessage(type: string, detail?: unknown, transfer?: Transferable[]) {
    const target = this.#iframe?.contentWindow
    if (!target) return

    const payload: IframeMessage = { type, detail }

    if (transfer?.length) target.postMessage(payload, iframeOrigin, transfer)
    else target.postMessage(payload, iframeOrigin)
  }

  #requestAnonymized(captureId: string) {
    if (!this.#iframe) return Promise.reject(new Error("[LIQA] Iframe is not available."))

    const requestId = `anonymized-${++this.#anonymizedRequestId}`

    return new Promise<AnonymizedResult>((resolve, reject) => {
      this.#pendingAnonymized.set(requestId, { resolve, reject })

      const configured = this.#iframeConfigured?.promise
      if (configured) configured.then(() => this.#postMessage("anonymized", { requestId, captureId }))
      else this.#postMessage("anonymized", { requestId, captureId })
    })
  }

  #handleIframeMessage = (event: MessageEvent<IframeMessage>) => {
    if (!this.#iframe || event.source !== this.#iframe.contentWindow) return
    if (!isMessage(event.data)) return
    if (event.origin !== iframeOrigin) return

    const { type, detail } = event.data

    if (type === "load") {
      if (this.#iframeConfig) this.#postMessage("config", this.#iframeConfig)
      this.#iframeConfigured?.resolve()
      return
    }

    if (type === "ready") {
      this.dispatchEvent(new CustomEvent(Events.LOADED))
      this.dispatchEvent(new CustomEvent(Events.READY))
      return
    }

    if (type === "analytics") {
      this.dispatchEvent(new CustomEvent(Events.ANALYTICS, { detail }))
      return
    }

    if (type === "error") {
      const error = deserializeError(detail)
      this.dispatchEvent(new CustomEvent(Events.ERROR, { detail: error }))
      return
    }

    if (type === "captures") {
      const captures = (detail as { captures?: SerializedCapture[] })?.captures ?? []
      const restored = captures.map((capture) =>
        createCaptureProxy({
          serialized: capture,
          requestAnonymized: (id) => this.#requestAnonymized(id),
        }),
      )

      this.dispatchEvent(new CustomEvent(Events.CAPTURES, { detail: restored }))
      for (const capture of restored)
        this.dispatchEvent(new CustomEvent(Events.CAPTURE, { detail: capture }))

      return
    }

    if (type === "anonymized") {
      const { requestId, result, error } = detail as Partial<{
        requestId: string
        result: SerializedAnonymized
        error: unknown
      }>

      if (!requestId) return

      const pending = this.#pendingAnonymized.get(requestId)
      if (!pending) return

      this.#pendingAnonymized.delete(requestId)

      if (result) pending.resolve(deserializeAnonymized(result))
      else pending.reject(deserializeError(error ?? "Failed to resolve anonymized data"))
    }
  }

  #createIframeWidget(config: AppConfig): Promise<Widget> {
    this.#iframeConfig = config
    this.#iframeConfigured = createDeferred()
    this.#pendingAnonymized.clear()

    const iframe = document.createElement("iframe")
    iframe.className = "liqa-iframe"
    iframe.title = "LIQA"
    iframe.src = iframeUrl
    iframe.allow = "camera; microphone"
    iframe.sandbox.add("allow-scripts", "allow-forms", "allow-same-origin")
    iframe.addEventListener(
      "load",
      () => {
        this.#postMessage("init", { v: import.meta.env.VERSION })
      },
      { once: true },
    )

    addEventListener("message", this.#handleIframeMessage)
    this.#iframe = iframe
    this.#root.appendChild(iframe)

    return Promise.resolve({
      destroy: () => {
        removeEventListener("message", this.#handleIframeMessage)

        for (const pending of this.#pendingAnonymized.values())
          pending.reject(
            new Error("[LIQA] Iframe was removed before anonymized data was received."),
          )

        this.#pendingAnonymized.clear()
        this.#iframeConfigured = null
        this.#iframeConfig = null
        this.#iframe?.remove()
        this.#iframe = null
      },
      reset: () => {
        for (const pending of this.#pendingAnonymized.values())
          pending.reject(new Error("[LIQA] Session was reset before anonymized data was received."))

        this.#pendingAnonymized.clear()

        const ready = this.#iframeConfigured?.promise ?? Promise.resolve()
        ready.then(() => this.#postMessage("reset"))
      },
      upload: (files) => {
        const ready = this.#iframeConfigured?.promise ?? Promise.resolve()
        ready.then(() => this.#postMessage("upload", { files }))
      },
    })
  }

  #createShadowWidget(attrs: LiqaAttributes & { useIframe?: string | boolean }): Promise<Widget> {
    // Isolated JavaScript execution context
    const realm = new ShadowRealm(this.#root)

    if (this.#isE2e)
      realm["_getGlobal"]().then((realm) => {
        // use global getUserMedia instead of the isolated one from the Realm to allow test environment to mock it
        Object.getPrototypeOf(realm.navigator.mediaDevices).getUserMedia = Object.getPrototypeOf(
          self.navigator.mediaDevices,
        ).getUserMedia
      })

    return Promise.resolve()
      .then(async () => ({
        // enable global error tracking
        teardownErrors: (
          await realm.importValue<typeof import("@/libs/telemetry/errors").setup>(
            Vite.resolve(errors.js),
            "setup",
          )
        )(),
      }))
      .then(async ({ teardownErrors }) => {
        // Inject the dynamically imported by Vite CSS styles into the shadowRoot instead of the Realm‘s document.head
        // https://github.com/vitejs/vite/issues/11855#issuecomment-1762258886
        realm["_getGlobal"]().then((realm) => Vite.adoptStyles(realm.document.head, this.#root))

        const create = await Vite.preload(
          () => realm.importValue<typeof import("@/root").create>(Vite.resolve(liqa.js), "create"),
          liqa.css,
          import.meta.url,
          this.#root,
        )

        const component = create(this.#root, buildConfig(attrs))

        return {
          destroy: () => {
            teardownErrors()
            component?.destroy()
          },
          reset: () => component?.reset(),
          upload: (files: File[]) => component?.upload(files),
        }
      })
      .catch((error) => {
        // track any errors during the component loading and initialization
        const license = this.attributes.getNamedItem("license")?.value

        realm
          .importValue(Vite.resolve(error.js), "log")
          .then((log) => log(error, { tags: { license } }))

        throw error
      })
  }

  protected connectedCallback() {
    // https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_custom_elements#using_the_lifecycle_callbacks
    if (!this.isConnected) return

    if (this.#widget) return

    const attrs = attrs2props<LiqaAttributes & { useIframe?: string | boolean }>(this.attributes)
    const useIframe = isTruthyAttr(attrs.useIframe)
    if (useIframe) {
      const props = buildConfig(attrs)
      const iframeStyles = buildIframeStyles(this, props.styles)
      const iframeConfig = iframeStyles ? { ...props, styles: iframeStyles } : props

      this.#widget = this.#createIframeWidget(iframeConfig)
      return
    }

    this.#widget = this.#createShadowWidget(attrs)
  }

  protected disconnectedCallback() {
    if (!this.#widget) return

    const children = new Set(this.#root.children)

    this.#widget.then((widget) => {
      widget.destroy()

      for (const child of this.#root.children) {
        if (children.has(child)) this.#root.removeChild(child)
      }
    })
    this.#widget = null
  }

  /**
   * Programmatically uploads one or more files to LIQA, bypassing the manual file selection UI.
   * @param files - An array of File objects to upload.
   * @example
   * ```js
   * // Upload a single file
   * const fileInput = document.querySelector('#file-input')
   * const files = Array.from(fileInput.files)
   * liqa.upload(files)
   *
   * // Upload multiple files programmatically
   * const files = [file1, file2, file3]
   * liqa.upload(files)
   * ```
   */
  protected upload(files: File[]): void {
    this.#widget?.then((widget) => widget.upload?.(files))
  }

  /**
   * Programmatically resets LIQA to its initial state, clearing any captured images and stopping the camera.
   * @example
   * ```js
   * // Reset LIQA to start over
   * liqa.reset()
   * ```
   */
  protected reset(): void {
    this.#widget?.then((widget) => widget.reset?.())
  }

  /** @internal */
  override addEventListener(...args: unknown[]): void {
    if (args[0] === Events.LOADED || args[0] === Events.CAPTURE) {
      console.warn(
        `[LIQA] Deprecation warning: the "${args[0]}" event is deprecated. Subscribe to "${args[0] === Events.LOADED ? Events.READY : Events.CAPTURES}" event instead.`,
      )
    }

    super.addEventListener(...(args as [string, EventListenerOrEventListenerObject]))
  }
}

export interface LiqaElement {
  /** Subscribes the event listener to the given LIQA {@link EventMap event}. */
  addEventListener<K extends keyof EventMap>(
    type: K,
    listener: (this: LiqaElement, ev: CustomEvent<EventMap[K]>) => any,
    options?: boolean | AddEventListenerOptions,
  ): void
  /** @hidden */
  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void
  /** Unsubscribes the event listener from the given LIQA {@link EventMap event}. */
  removeEventListener<K extends keyof EventMap>(
    type: K,
    listener: (this: LiqaElement, ev: CustomEvent<EventMap[K]>) => any,
    options?: boolean | EventListenerOptions,
  ): void
  /** @hidden */
  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ): void
}
